import { ApiErrors } from "../backend/api-errors/errors";
import { Investment } from "../backend/generated/investment-model";
import { Payment } from "../backend/generated/payment-model";
import {
  APPROVE_USER,
  CREATE_INVESTMENT,
  CREATE_PAYMENT,
  CREATE_SALE,
  CREATE_USER,
  DELETE_PAYMENT,
  GENERATE_UPLOAD_SIGNED_URL,
  GET_CURRENT_USER,
  GET_CURRENT_USER_INVESTMENTS,
  GET_IDNOW_USER,
  GET_INVESTMENTS,
  GET_SALES,
  GET_USERS,
  LOGIN_USER,
  REGISTER_USER,
  UPDATE_INVESTMENT,
  UPDATE_USER,
} from "../backend/graphql/queries";
import { STORE_INVESTMENTS_ACTION, STORE_UPDATE_INVESTMENT_ACTION } from "../store/investment/actions";
import { InvestmentManageable } from "../store/investment/types";
import { STORE_SALES_ACTION } from "../store/sale/actions";
import { SaleManageable } from "../store/sale/types";
import {
  LOGIN_ACTION,
  LOGOUT_ACTION,
  STORE_CURRENT_USER_ACTION,
} from "../store/user/actions";
import { UserManageable } from "../store/user/types";

export enum QueryType {
  Query,
  LazyQuery,
  Mutation
}

export interface QueryDescriptor {
  fetchPolicy?: string;
  input?: (storageData: any) => any;
  onError?: (error: ApiErrors, dispatch: any) => void;
  onSuccess?: (data: any, dispatch: any, storageData: any) => void;
  pollInterval?: number;
  query: any;
  skip?: (storageData: any) => boolean;
  type: QueryType;
}

/* QUERIES */

export const GetUsers = ({ onSuccess }: any): QueryDescriptor => {
  return {
    type: QueryType.Query,
    query: GET_USERS,
    pollInterval: 25000,
    onSuccess: (data: any) => {
      if (onSuccess) {
        onSuccess(data.getUsers);
      }
    },
  };
};

export const GetCurrentUser = ({ onSuccess }: any): QueryDescriptor => {
  return {
    type: QueryType.Query,
    query: GET_CURRENT_USER,
    onError: (
      _: ApiErrors, dispatch: any
    ) => {
      dispatch({ type: LOGOUT_ACTION });
    },
    onSuccess: (
      data: any, dispatch: any
    ) => {
      const userDetails = new UserManageable(data.getCurrentUser);

      dispatch({
        type: STORE_CURRENT_USER_ACTION,
        payload: userDetails,
      });

      if (onSuccess) {
        onSuccess(data.getCurrentUser);
      }
    },
    pollInterval: 25000,
  };
}

export const GetCurrentUserInvestments = ({ onSuccess }: any): QueryDescriptor => {
  return {
    type: QueryType.Query,
    query: GET_CURRENT_USER_INVESTMENTS,
    onSuccess: (
      data: any, dispatch: any
    ) => {
      dispatch({
        type: STORE_INVESTMENTS_ACTION,
        payload: { investments: data.getCurrentUserInvestments.map((investment: Investment) => new InvestmentManageable(investment)) },
      });

      if (onSuccess) {
        onSuccess(data.getInvestments);
      }
    },
    pollInterval: 25000,
  };
}

export const GetIdNowUser = ({
  input,
  onSuccess,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Query,
    input: () => input,
    query: GET_IDNOW_USER,
    onSuccess: (data: any) => {
      if (onSuccess) {
        onSuccess(data.getIdNowUser);
      }
    },
  };
}

export const GetInvestments = ({
  input,
  onSuccess,
}: any): QueryDescriptor => {
  return {
    fetchPolicy: 'network-only',
    type: QueryType.Query,
    input: () => input,
    query: GET_INVESTMENTS,
    onSuccess: (data: any) => {
      if (onSuccess) {
        onSuccess(data.getInvestments);
      }
    },
  };
}

export const GetSales = ({ onSuccess }: any): QueryDescriptor => {
  return {
    type: QueryType.LazyQuery,
    query: GET_SALES,
    onSuccess: (
      data: any, dispatch: any
    ) => {
      dispatch({
        type: STORE_SALES_ACTION,
        payload: { sales: data.getSales.map((sale: any) => new SaleManageable(sale)) },
      });

      if (onSuccess) {
        onSuccess(data.getSales);
      }
    },
  };
}

/* LAZY QUERIES */

export const GetCurrentUserLazy = ({
  onSuccess,
  onError,
}: any): QueryDescriptor => {
  return {
    fetchPolicy: 'network-only',
    type: QueryType.LazyQuery,
    query: GET_CURRENT_USER,
    onSuccess: (
      data: any, dispatch: any
    ) => {
      let userDetails = new UserManageable(data.getCurrentUser);

      dispatch({
        type: STORE_CURRENT_USER_ACTION,
        payload: userDetails,
      });

      if (onSuccess) {
        onSuccess(data.getCurrentUser);
      }
    },
    onError: (error: any) => {
      if (onError) {
        onError(error);
      }
    },
  };
}

/* MUTATIONS */

export const ApproveUser = ({ onSuccess }: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: APPROVE_USER,
    onSuccess: (data: any) => {
      onSuccess(data.approveUser);
    },
  };
}

export const CreateInvestment = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: CREATE_INVESTMENT,
    onError: (error: ApiErrors) => {
      onError(error);
    },
    onSuccess: (
      data: any, dispatch: any, storageData: any
    ) => {
      let userDetails = new UserManageable({ ...storageData });

      if (!userDetails.investments) {
        userDetails.investments = [];
      }
      userDetails.investments.push(data.createInvestment);

      dispatch({
        type: STORE_CURRENT_USER_ACTION,
        payload: userDetails,
      });

      if (onSuccess) {
        onSuccess(data.createInvestment);
      }
    },
  };
}

export const CreatePayment = ({ onSuccess }: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: CREATE_PAYMENT,
    onSuccess: (
      data: any, dispatch: any, storageData: any
    ) => {
      let userDetails = new UserManageable(storageData);
      if (!userDetails.payments) {
        userDetails.payments = [];
      }
      userDetails.payments.push(data.createPayment);

      if (userDetails.payments.length === 1) {
        userDetails.defaultPayment = data.createPayment.id;
      }

      dispatch({
        type: STORE_CURRENT_USER_ACTION,
        payload: userDetails,
      });

      onSuccess(data.createPayment);
    },
  };
}

export const CreateSale = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: CREATE_SALE,
    onError: (error: ApiErrors) => {
      onError(error);
    },
    onSuccess: (data: any) => {
      onSuccess(data.createSale);
    },
  };
}

export const CreateUser = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: CREATE_USER,
    onError: (error: ApiErrors) => {
      onError(error);
    },
    onSuccess: (
      data: any, dispatch: any
    ) => {
      dispatch({
        type: STORE_CURRENT_USER_ACTION,
        payload: new UserManageable(data.createUser),
      });

      if (onSuccess) {
        onSuccess(data.createUser);
      }
    },
  };
}

export const DeletePayment = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: DELETE_PAYMENT,
    onError: (error: ApiErrors) => {
      onError(error);
    },
    onSuccess: (
      data: any, dispatch: any, storageData: any
    ) => {
      let userDetails = new UserManageable(storageData);
      if (userDetails.payments) {
        userDetails.payments = userDetails.payments.filter((account: Payment) => account.id !== data.deletePayment.id);
      }

      dispatch({
        type: STORE_CURRENT_USER_ACTION,
        payload: userDetails,
      });

      if (onSuccess) {
        onSuccess(data.deletePayment);
      }
    },
  };
}

export const GenerateUploadSignedURL = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: GENERATE_UPLOAD_SIGNED_URL,
    onError: (error: ApiErrors) => {
      if (onError) {
        onError(error);
      }
    },
    onSuccess: (data: any) => {
      if (onSuccess) {
        onSuccess(data.generateUploadSignedUrl);
      }
    },
  };
}

export const LoginUser = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: LOGIN_USER,
    onError: (error: ApiErrors) => {
      onError(error);
    },
    onSuccess: (
      data: any, dispatch: any
    ) => {
      dispatch({
        type: LOGIN_ACTION,
        payload: { token: data.loginUser },
      });

      if (onSuccess) {
        onSuccess(data.loginUser);
      }
    },
  };
}

export const RegisterUser = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: REGISTER_USER,
    onError: (error: ApiErrors) => {
      onError(error);
    },
    onSuccess: (
      data: any, dispatch: any
    ) => {
      dispatch({
        type: LOGIN_ACTION,
        payload: { token: data.registerUser },
      });

      if (onSuccess) {
        onSuccess(data.registerUser);
      }
    },
  };
}

export const UpdateInvestment = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: UPDATE_INVESTMENT,
    onError: (error: ApiErrors) => {
      if (onError) {
        onError(error);
      }
    },
    onSuccess: (
      data: any, dispatch: any
    ) => {
      dispatch({
        type: STORE_UPDATE_INVESTMENT_ACTION,
        payload: new InvestmentManageable(data.updateInvestment),
      });

      if (onSuccess) {
        onSuccess(data.updateInvestment);
      }
    },
  };
}

export const UpdateUser = ({ onSuccess }: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: UPDATE_USER,
    onSuccess: (
      data: any, dispatch: any
    ) => {
      dispatch({
        type: STORE_CURRENT_USER_ACTION,
        payload: new UserManageable(data.updateUser),
      });

      onSuccess(data.updateUser);
    },
  };
}
